/**
 * Pure helpers for the orders-inbox bulk-select feature. Extracted from
 * PortalOrdersPage so the selection logic stays testable without React.
 */
import type { OrderRecord, OrderStatus } from '../../types/orders';

/**
 * Toggle one id's membership in a Set without mutating the original.
 * Used by individual row checkbox handlers.
 */
export function toggleSelection(
  current: ReadonlySet<string>,
  id: string,
): Set<string> {
  const next = new Set(current);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}

/**
 * Select-all-on-page semantics: if every visible order is already
 * selected, clicking the header checkbox deselects them; otherwise it
 * selects all visible. Selections from previous pages are preserved.
 */
export function toggleSelectAll(
  current: ReadonlySet<string>,
  visibleIds: readonly string[],
): Set<string> {
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => current.has(id));
  const next = new Set(current);
  if (allSelected) {
    for (const id of visibleIds) next.delete(id);
  } else {
    for (const id of visibleIds) next.add(id);
  }
  return next;
}

/** Select-all checkbox tri-state for the column header. */
export type SelectAllState = 'none' | 'some' | 'all';

export function selectAllState(
  current: ReadonlySet<string>,
  visibleIds: readonly string[],
): SelectAllState {
  if (visibleIds.length === 0) return 'none';
  let selectedCount = 0;
  for (const id of visibleIds) if (current.has(id)) selectedCount += 1;
  if (selectedCount === 0) return 'none';
  if (selectedCount === visibleIds.length) return 'all';
  return 'some';
}

/**
 * Drop ids that are no longer visible in the current page from the
 * selection — keeps the bulk-bar count honest after filter / search /
 * pagination changes that drop rows.
 */
export function pruneSelection(
  current: ReadonlySet<string>,
  visibleIds: readonly string[],
): Set<string> {
  const visible = new Set(visibleIds);
  const next = new Set<string>();
  for (const id of current) if (visible.has(id)) next.add(id);
  return next;
}

/**
 * Of the currently selected (and currently visible) orders, count how
 * many are draft — only drafts can be bulk-dismissed today.
 */
export function countDraftSelected(
  current: ReadonlySet<string>,
  orders: readonly OrderRecord[],
): number {
  const DRAFT: OrderStatus = 'draft';
  let n = 0;
  for (const o of orders) if (current.has(o.id) && o.status === DRAFT) n += 1;
  return n;
}

/** Subset of the selection that maps to draft orders in the visible page. */
export function selectedDraftIds(
  current: ReadonlySet<string>,
  orders: readonly OrderRecord[],
): string[] {
  const DRAFT: OrderStatus = 'draft';
  return orders
    .filter((o) => current.has(o.id) && o.status === DRAFT)
    .map((o) => o.id);
}
